import { CognitoUser } from "utils/cognito-auth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Auth } from "utils/cognito-auth";
import { supportURL } from "common/const";
import { ReactQueryMutationError } from "data/mutations-common";
import { ChangeEvent, useState } from "react";
import { useMutation } from "@tanstack/react-query";

interface MfaLoginProps {
  user: CognitoUser | null;
  onSubmit: () => Promise<void>;
}

export const MfaLogin = (props: MfaLoginProps) => {
  const [mfaCode, setMfaCode] = useState("");

  const [challengeName] = useState(props.user?.challengeName);

  const confirmSignInMutation = useMutation({
    mutationFn: () => {
      return Auth.confirmSignIn(
        props.user,
        mfaCode,
        challengeName as "SMS_MFA" | "SOFTWARE_TOKEN_MFA" | null,
      );
    },
    onSuccess: async () => {
      await props.onSubmit();
    },
  });

  const onCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value;
    setMfaCode(code.replace(/[^0-9]/g, ""));
  };

  const isMfaLoading = confirmSignInMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Multi Factor Authentication</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {challengeName === "SOFTWARE_TOKEN_MFA" && (
          <p className="text-sm">
            To continue, please enter the six digit code generated by your
            authenticator app.
          </p>
        )}
        {challengeName === "SMS_MFA" && (
          <p className="text-sm">
            We just sent you a message via SMS with your authentication code.
            Enter the code below to verify your identity.
          </p>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="verificationCode">Verification Code</Label>
          <Input
            id="verificationCode"
            maxLength={6}
            value={mfaCode}
            onChange={onCodeChange}
            onKeyDown={(ev) => {
              if (ev.key === "Enter" && mfaCode.length === 6)
                confirmSignInMutation.mutate();
            }}
          />
        </div>
        {confirmSignInMutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {(confirmSignInMutation.error as ReactQueryMutationError).message}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
        <Button
          className="w-full"
          disabled={mfaCode.length !== 6}
          onClick={() => confirmSignInMutation.mutate()}
        >
          {isMfaLoading ? <Spinner /> : "Verify"}
        </Button>
        <Button variant="link" className="w-full" asChild>
          <a href={supportURL} target="_blank" rel="noopener noreferrer">
            Contact Support
          </a>
        </Button>
      </CardFooter>
    </Card>
  );
};
